// src/pages/ColdChainTracking.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "./SensorDashboard.css";
import "./ColdChainTracking.css";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/sensores`;
const TZ = "America/Guayaquil";

const RANGE_MS = {
  "1h": 1 * 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
};

// El GPS puede variar varios metros aunque el vehículo esté detenido.
// El recorrido usa 20 m por defecto, pero el usuario puede ajustarlo.
const DEFAULT_MIN_MOVEMENT_M = 20;

function toLocalInputValue(date) {
  const d = new Date(date);
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function toMsSafe(value) {
  if (value == null) return NaN;
  if (typeof value === "number") return value < 1e11 ? value * 1000 : value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function fmtDT(value) {
  const ms = toMsSafe(value);
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString("es-EC", {
    timeZone: TZ,
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function fmtNumber(value, decimals = 1) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(decimals) : "—";
}

function haversineKm(a, b) {
  if (!a || !b) return 0;
  const lat1 = Number(a.lat);
  const lon1 = Number(a.lon);
  const lat2 = Number(b.lat);
  const lon2 = Number(b.lon);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return 0;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const r = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLon / 2);
  const h = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
}

function rangeLabel(mode) {
  if (mode === "1h") return "Última hora";
  if (mode === "6h") return "Últimas 6 horas";
  if (mode === "24h") return "Últimas 24 horas";
  if (mode === "7d") return "Últimos 7 días";
  return "Rango personalizado";
}

export default function ColdChainTracking() {
  const { sensorId = "" } = useParams();
  const navigate = useNavigate();

  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const viewAs = query.get("as") || "";

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [sensor, setSensor] = useState(null);
  const [points, setPoints] = useState([]);
  const [latest, setLatest] = useState(null);
  const [rangeMode, setRangeMode] = useState("24h");
  const [customFrom, setCustomFrom] = useState(() =>
    toLocalInputValue(new Date(Date.now() - RANGE_MS["24h"]))
  );
  const [customTo, setCustomTo] = useState(() => toLocalInputValue(new Date()));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [minMovementM, setMinMovementM] = useState(DEFAULT_MIN_MOVEMENT_M);
  const [trackMeta, setTrackMeta] = useState({ rawCount: 0, routeCount: 0 });
  const [serverStats, setServerStats] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [alarmPoints, setAlarmPoints] = useState([]);
  const [thresholds, setThresholds] = useState({ TEMP: { min: null, max: null }, HUM: { min: null, max: null } });
  const [onlyAlarms, setOnlyAlarms] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const pointLayerRef = useRef(null);
  const alarmLayerRef = useRef(null);
  const startLayerRef = useRef(null);
  const endLayerRef = useRef(null);

  const withAs = useCallback(
    (url) => {
      if (!viewAs) return url;
      return `${url}${url.includes("?") ? "&" : "?"}as=${encodeURIComponent(viewAs)}`;
    },
    [viewAs]
  );

  const resolveRange = useCallback(() => {
    if (rangeMode === "custom") {
      const from = new Date(customFrom);
      const to = new Date(customTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        throw new Error("Selecciona un rango de fechas válido.");
      }
      if (from.getTime() > to.getTime()) {
        throw new Error("La fecha inicial no puede ser mayor que la final.");
      }
      return { from, to };
    }

    const to = new Date();
    const from = new Date(to.getTime() - RANGE_MS[rangeMode]);
    return { from, to };
  }, [rangeMode, customFrom, customTo]);

  const loadTrack = useCallback(async () => {
    if (!sensorId) return;

    try {
      setLoading(true);
      setError("");

      const { from, to } = resolveRange();
      const url = withAs(
        `${API_BASE}/cold-chain/${encodeURIComponent(sensorId)}/track` +
          `?from=${encodeURIComponent(from.toISOString())}` +
          `&to=${encodeURIComponent(to.toISOString())}` +
          `&limit=5000` +
          `&minDistanceM=${encodeURIComponent(minMovementM)}`
      );

      const { data } = await axios.get(url, authHeader);
      const nextPoints = Array.isArray(data?.points) ? data.points : [];
      setSensor(data?.sensor || null);
      setPoints(nextPoints);
      setTrackMeta({
        rawCount: Number(data?.raw_count ?? nextPoints.length) || 0,
        routeCount: Number(data?.count ?? nextPoints.length) || 0,
      });
      setServerStats(data?.stats || null);
      setAlarmPoints(Array.isArray(data?.alarm_points) ? data.alarm_points : []);
      setThresholds(data?.thresholds || { TEMP: { min: null, max: null }, HUM: { min: null, max: null } });
      setLastRefresh(Date.now());
    } catch (err) {
      setPoints([]);
      setTrackMeta({ rawCount: 0, routeCount: 0 });
      setServerStats(null);
      setAlarmPoints([]);
      setThresholds({ TEMP: { min: null, max: null }, HUM: { min: null, max: null } });
      setError(
        err?.response?.data?.error || err?.message || "No se pudo cargar el recorrido."
      );
    } finally {
      setLoading(false);
    }
  }, [sensorId, resolveRange, withAs, authHeader, minMovementM]);

  const loadLatest = useCallback(async () => {
    if (!sensorId) return;

    try {
      const url = withAs(
        `${API_BASE}/cold-chain/${encodeURIComponent(sensorId)}/latest`
      );
      const { data } = await axios.get(url, authHeader);
      if (data?.sensor) setSensor(data.sensor);
      if (data?.thresholds) setThresholds(data.thresholds);
      setLatest(data?.point || null);
      setLastRefresh(Date.now());
    } catch (_) {
      // El recorrido puede seguir visible aunque falle una actualización puntual.
    }
  }, [sensorId, withAs, authHeader]);

  useEffect(() => {
    loadTrack();
    loadLatest();
  }, [loadTrack, loadLatest]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const latestId = setInterval(loadLatest, 20000);
    const trackId = setInterval(loadTrack, 60000);

    return () => {
      clearInterval(latestId);
      clearInterval(trackId);
    };
  }, [autoRefresh, loadLatest, loadTrack]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      scrollWheelZoom: true,
      zoomControl: true,
    }).setView([-2.17, -79.9], 11);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(mapRef.current);

    const resizeId = setTimeout(() => mapRef.current?.invalidateSize(), 150);

    return () => {
      clearTimeout(resizeId);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      currentMarkerRef.current = null;
      routeLayerRef.current = null;
      pointLayerRef.current = null;
      alarmLayerRef.current = null;
      startLayerRef.current = null;
      endLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
      routeLayerRef.current = null;
    }
    if (pointLayerRef.current) {
      pointLayerRef.current.remove();
      pointLayerRef.current = null;
    }
    if (alarmLayerRef.current) {
      alarmLayerRef.current.remove();
      alarmLayerRef.current = null;
    }
    if (startLayerRef.current) {
      startLayerRef.current.remove();
      startLayerRef.current = null;
    }
    if (endLayerRef.current) {
      endLayerRef.current.remove();
      endLayerRef.current = null;
    }

    const valid = points.filter(
      (p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lon))
    );
    const validAlarms = alarmPoints.filter(
      (p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lon))
    );

    if (!valid.length && !validAlarms.length) return;

    if (valid.length >= 2) {
      const latLngs = valid.map((p) => [Number(p.lat), Number(p.lon)]);
      routeLayerRef.current = L.polyline(latLngs, {
        weight: 5,
        opacity: 0.82,
        color: "#2563eb",
      }).addTo(map);
    }

    if (!onlyAlarms && valid.length) {
      const pointGroup = L.layerGroup().addTo(map);
      pointLayerRef.current = pointGroup;
      const step = Math.max(1, Math.ceil(valid.length / 250));

      valid.forEach((p, index) => {
        if (p.is_alarm) return;
        if (index % step !== 0 && index !== valid.length - 1) return;

        const popup = `
          <div style="min-width:205px;font-family:Arial,sans-serif">
            <strong>${fmtDT(p.ts)}</strong><br/>
            Estado: <b>${p.status === "NORMAL" ? "NORMAL" : "SIN UMBRAL"}</b><br/>
            Temperatura: <b>${fmtNumber(p.temp, 1)} °C</b><br/>
            Humedad: <b>${fmtNumber(p.hum, 1)} %HR</b><br/>
            Lat/Lng: ${Number(p.lat).toFixed(6)} / ${Number(p.lon).toFixed(6)}
          </div>
        `;

        L.circleMarker([Number(p.lat), Number(p.lon)], {
          radius: 4,
          weight: 1,
          color: "#1d4ed8",
          fillColor: "#ffffff",
          fillOpacity: 0.92,
        })
          .bindPopup(popup)
          .addTo(pointGroup);
      });
    }

    if (validAlarms.length) {
      const alarmGroup = L.layerGroup().addTo(map);
      alarmLayerRef.current = alarmGroup;
      const alarmStep = Math.max(1, Math.ceil(validAlarms.length / 400));

      validAlarms.forEach((p, index) => {
        if (index % alarmStep !== 0 && index !== validAlarms.length - 1) return;

        const popup = `
          <div style="min-width:225px;font-family:Arial,sans-serif">
            <strong style="color:#b91c1c">⚠ FUERA DE RANGO</strong><br/>
            ${fmtDT(p.ts)}<br/>
            Temperatura: <b>${fmtNumber(p.temp, 1)} °C</b><br/>
            Humedad: <b>${fmtNumber(p.hum, 1)} %HR</b><br/>
            <span style="color:#b91c1c;font-weight:700">${p.alarm_detail || "Fuera de umbral"}</span><br/>
            Lat/Lng: ${Number(p.lat).toFixed(6)} / ${Number(p.lon).toFixed(6)}
          </div>
        `;

        L.circleMarker([Number(p.lat), Number(p.lon)], {
          radius: 7,
          weight: 2,
          color: "#991b1b",
          fillColor: "#ef4444",
          fillOpacity: 0.95,
        })
          .bindPopup(popup)
          .addTo(alarmGroup);
      });
    }

    if (valid.length) {
      const first = valid[0];
      const last = valid[valid.length - 1];

      startLayerRef.current = L.circleMarker([Number(first.lat), Number(first.lon)], {
        radius: 8,
        weight: 3,
        color: "#15803d",
        fillColor: "#22c55e",
        fillOpacity: 1,
      })
        .bindTooltip("Inicio del recorrido")
        .addTo(map);

      endLayerRef.current = L.circleMarker([Number(last.lat), Number(last.lon)], {
        radius: 8,
        weight: 3,
        color: "#9a3412",
        fillColor: "#f97316",
        fillOpacity: 1,
      })
        .bindTooltip("Último punto del rango")
        .addTo(map);
    }

    try {
      if (routeLayerRef.current) {
        map.fitBounds(routeLayerRef.current.getBounds(), {
          padding: [35, 35],
          maxZoom: 17,
        });
      } else if (validAlarms.length) {
        map.fitBounds(L.latLngBounds(validAlarms.map((p) => [Number(p.lat), Number(p.lon)])), {
          padding: [35, 35],
          maxZoom: 17,
        });
      }
    } catch (_) {}
  }, [points, alarmPoints, onlyAlarms]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !latest) return;

    const lat = Number(latest.lat);
    const lon = Number(latest.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    const popup = `
      <div style="min-width:190px;font-family:Arial,sans-serif">
        <strong>Posición actual</strong><br/>
        ${fmtDT(latest.ts)}<br/>
        Temperatura: <b>${fmtNumber(latest.temp, 1)} °C</b><br/>
        Humedad: <b>${fmtNumber(latest.hum, 1)} %HR</b>
      </div>
    `;

    if (!currentMarkerRef.current) {
      currentMarkerRef.current = L.marker([lat, lon])
        .bindPopup(popup)
        .addTo(map);
    } else {
      currentMarkerRef.current.setLatLng([lat, lon]);
      currentMarkerRef.current.setPopupContent(popup);
    }
  }, [latest]);

  const stats = useMemo(() => {
    const valid = points.filter(
      (p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lon))
    );

    let fallbackDistanceKm = 0;
    for (let i = 1; i < valid.length; i += 1) {
      fallbackDistanceKm += haversineKm(valid[i - 1], valid[i]);
    }

    const fallbackTemps = points.map((p) => Number(p.temp)).filter(Number.isFinite);
    const fallbackHums = points.map((p) => Number(p.hum)).filter(Number.isFinite);
    const avg = (arr) =>
      arr.length ? arr.reduce((sum, n) => sum + n, 0) / arr.length : null;

    const fallbackFirstTs = valid.length ? toMsSafe(valid[0].ts) : NaN;
    const fallbackLastTs = valid.length ? toMsSafe(valid[valid.length - 1].ts) : NaN;
    const fallbackDurationMs =
      Number.isFinite(fallbackFirstTs) && Number.isFinite(fallbackLastTs)
        ? Math.max(0, fallbackLastTs - fallbackFirstTs)
        : 0;

    const numberOr = (value, fallback) => {
      if (value === null || value === undefined || value === "") return fallback;
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    return {
      count: valid.length,
      rawCount: numberOr(serverStats?.raw_count, trackMeta.rawCount),
      alarmCount: numberOr(serverStats?.alarm_count, alarmPoints.length),
      distanceKm: numberOr(serverStats?.distance_km, fallbackDistanceKm),
      tempMin: numberOr(serverStats?.temp_min, fallbackTemps.length ? Math.min(...fallbackTemps) : null),
      tempMax: numberOr(serverStats?.temp_max, fallbackTemps.length ? Math.max(...fallbackTemps) : null),
      tempAvg: numberOr(serverStats?.temp_avg, avg(fallbackTemps)),
      humMin: numberOr(serverStats?.hum_min, fallbackHums.length ? Math.min(...fallbackHums) : null),
      humMax: numberOr(serverStats?.hum_max, fallbackHums.length ? Math.max(...fallbackHums) : null),
      humAvg: numberOr(serverStats?.hum_avg, avg(fallbackHums)),
      durationMs: numberOr(serverStats?.duration_ms, fallbackDurationMs),
    };
  }, [points, serverStats, trackMeta.rawCount, alarmPoints.length]);

  const current = latest || (points.length ? points[points.length - 1] : null);

  const liveStatus = useMemo(() => {
    const ts = toMsSafe(current?.ts);
    if (!Number.isFinite(ts)) return "offline";
    const age = Date.now() - ts;
    if (age <= 5 * 60 * 1000) return "online";
    if (age <= 30 * 60 * 1000) return "stale";
    return "offline";
  }, [current]);

  const durationText = useMemo(() => {
    const totalMin = Math.round(stats.durationMs / 60000);
    if (!totalMin) return "—";
    const hours = Math.floor(totalMin / 60);
    const minutes = totalMin % 60;
    return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
  }, [stats.durationMs]);

  const downloadRoute = useCallback(
    async (format) => {
      if (!sensorId || downloading) return;

      try {
        setDownloading(format);
        setError("");

        const { from, to } = resolveRange();
        const endpointMap = { xlsx: "export.xlsx", kml: "export.kml", pdf: "export.pdf" };
        const endpoint = endpointMap[format];
        if (!endpoint) throw new Error("Formato de descarga no válido.");
        const url = withAs(
          `${API_BASE}/cold-chain/${encodeURIComponent(sensorId)}/${endpoint}` +
            `?from=${encodeURIComponent(from.toISOString())}` +
            `&to=${encodeURIComponent(to.toISOString())}` +
            `&minDistanceM=${encodeURIComponent(minMovementM)}`
        );

        const response = await axios.get(url, {
          ...authHeader,
          responseType: "blob",
        });

        const disposition = response.headers?.["content-disposition"] || "";
        const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
        const fallbackName = `Recorrido_${String(sensor?.name || sensorId)
          .replace(/[^a-zA-Z0-9_-]+/g, "_")}.${format}`;
        const filename = filenameMatch?.[1] || fallbackName;

        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "No se pudo descargar el recorrido."
        );
      } finally {
        setDownloading("");
      }
    },
    [
      sensorId,
      downloading,
      resolveRange,
      withAs,
      minMovementM,
      authHeader,
      sensor?.name,
    ]
  );

  const thresholdText = useCallback((threshold, unit) => {
    const min = threshold?.min === null || threshold?.min === undefined ? null : Number(threshold.min);
    const max = threshold?.max === null || threshold?.max === undefined ? null : Number(threshold.max);
    const hasMin = Number.isFinite(min);
    const hasMax = Number.isFinite(max);
    if (!hasMin && !hasMax) return "Sin umbral";
    if (hasMin && hasMax) return `${min.toFixed(1)} a ${max.toFixed(1)} ${unit}`;
    if (hasMin) return `≥ ${min.toFixed(1)} ${unit}`;
    return `≤ ${max.toFixed(1)} ${unit}`;
  }, []);

  const listPoints = useMemo(
    () => (onlyAlarms ? alarmPoints : points),
    [onlyAlarms, alarmPoints, points]
  );

  const applyCustomRange = () => {
    try {
      const from = new Date(customFrom);
      const to = new Date(customTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        setError("Selecciona un rango de fechas válido.");
        return;
      }
      if (from.getTime() > to.getTime()) {
        setError("La fecha inicial no puede ser mayor que la final.");
        return;
      }
      if (to.getTime() - from.getTime() > 31 * 24 * 60 * 60 * 1000) {
        setError("El rango máximo permitido es de 31 días.");
        return;
      }
      setError("");
      setRangeMode("custom");
    } catch (_) {
      setError("No se pudo aplicar el rango seleccionado.");
    }
  };

  const setPreset = (mode) => {
    setRangeMode(mode);
    const now = new Date();
    setCustomTo(toLocalInputValue(now));
    setCustomFrom(toLocalInputValue(new Date(now.getTime() - RANGE_MS[mode])));
  };

  const goBack = () => {
    navigate(
      `/dashboard/HumedadTemperaturaGPS?sensorId=${encodeURIComponent(sensorId)}${
        viewAs ? `&as=${encodeURIComponent(viewAs)}` : ""
      }`
    );
  };

  const sensorImage = sensor?.image_url || sensor?.photo_url || null;

  return (
    <div className="sensor-dashboard cold-chain-page">
      <div className="cold-chain-floating-actions">
        <button type="button" className="cold-chain-icon-btn" onClick={goBack} title="Volver al sensor">
          ←
        </button>
        <button
          type="button"
          className="cold-chain-icon-btn"
          onClick={() => navigate("/seleccionar-tipo")}
          title="Inicio"
        >
          ⌂
        </button>
      </div>

      <header className="cold-chain-header">
        <div>
          <div className="cold-chain-eyebrow">TRAZABILIDAD DE CADENA DE FRÍO</div>
          <h1>{sensor?.name || sensorId || "Tracking GPS"}</h1>
          <div className="cold-chain-subtitle">
            ID: <strong>{sensorId || "—"}</strong> · {rangeLabel(rangeMode)}
          </div>
        </div>

        <div className={`cold-chain-live-badge ${liveStatus}`}>
          <span className="cold-chain-live-dot" />
          {liveStatus === "online"
            ? "EN LÍNEA"
            : liveStatus === "stale"
            ? "INTERMITENTE"
            : "FUERA DE LÍNEA"}
        </div>
      </header>

      <section className="cold-chain-controls card glass">
        <div className="cold-chain-presets">
          {["1h", "6h", "24h", "7d"].map((mode) => (
            <button
              key={mode}
              type="button"
              className={rangeMode === mode ? "active" : ""}
              onClick={() => setPreset(mode)}
            >
              {mode === "1h" ? "1 hora" : mode === "6h" ? "6 horas" : mode === "24h" ? "24 horas" : "7 días"}
            </button>
          ))}
        </div>

        <div className="cold-chain-custom-range">
          <label>
            Desde
            <input
              type="datetime-local"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              type="datetime-local"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </label>
          <button type="button" className="cold-chain-apply-btn" onClick={applyCustomRange}>
            APLICAR RANGO
          </button>
        </div>

        <label
          style={{
            display: "grid",
            gap: 4,
            fontSize: 10,
            fontWeight: 900,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: ".35px",
          }}
        >
          Movimiento mínimo
          <select
            value={minMovementM}
            onChange={(e) => setMinMovementM(Number(e.target.value))}
            style={{
              minHeight: 38,
              border: "1px solid #cbd5e1",
              borderRadius: 11,
              background: "#fff",
              color: "#334155",
              padding: "0 30px 0 10px",
              fontWeight: 850,
            }}
            title="Los cambios de posición menores a este valor se ignoran en la ruta para reducir el ruido del GPS."
          >
            <option value={0}>Sin filtro</option>
            <option value={10}>10 m</option>
            <option value={20}>20 m</option>
            <option value={30}>30 m</option>
            <option value={50}>50 m</option>
          </select>
        </label>

        <label className="cold-chain-auto-refresh" title="Oculta los puntos normales y deja visibles únicamente los eventos fuera de los umbrales.">
          <input
            type="checkbox"
            checked={onlyAlarms}
            onChange={(e) => setOnlyAlarms(e.target.checked)}
          />
          Solo fuera de rango ({stats.alarmCount || 0})
        </label>

        <label className="cold-chain-auto-refresh">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Tracking automático
        </label>
      </section>

      {error && <div className="cold-chain-error">{error}</div>}

      <section className="cold-chain-kpis">
        <div className="card glass cold-chain-kpi">
          <span>Temperatura actual</span>
          <strong>{fmtNumber(current?.temp, 1)} °C</strong>
          <small>Min/Max del rango: {fmtNumber(stats.tempMin, 1)} / {fmtNumber(stats.tempMax, 1)} °C</small>
        </div>

        <div className="card glass cold-chain-kpi">
          <span>Humedad actual</span>
          <strong>{fmtNumber(current?.hum, 1)} %HR</strong>
          <small>Min/Max del rango: {fmtNumber(stats.humMin, 1)} / {fmtNumber(stats.humMax, 1)} %HR</small>
        </div>

        <div className="card glass cold-chain-kpi">
          <span>Distancia recorrida</span>
          <strong>{fmtNumber(stats.distanceKm, 2)} km</strong>
          <small>
            {stats.count} puntos de ruta · {stats.rawCount} GPS recibidos · {stats.alarmCount} alarmas · {durationText}
          </small>
        </div>

        <div className="card glass cold-chain-kpi">
          <span>Última posición</span>
          <strong className="cold-chain-coord-value">
            {Number.isFinite(Number(current?.lat)) ? Number(current.lat).toFixed(6) : "—"}
          </strong>
          <small>
            {Number.isFinite(Number(current?.lon)) ? Number(current.lon).toFixed(6) : "—"} · {fmtDT(current?.ts)}
          </small>
        </div>
      </section>

      <section className="cold-chain-main-grid">
        <div className="card glass cold-chain-info-card">
          <div className="cold-chain-sensor-head">
            <div className="cold-chain-image-box">
              {sensorImage ? (
                <img
                  src={sensorImage}
                  alt={sensor?.name || sensorId}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span>GPS</span>
              )}
            </div>

            <div>
              <h2>{sensor?.name || sensorId || "Sensor"}</h2>
              <div className="cold-chain-meta">Tipo: {sensor?.type || "HumedadTemperaturaGPS"}</div>
              <div className="cold-chain-meta">Actualización: {fmtDT(current?.ts)}</div>
              <div className="cold-chain-meta">Rango: {rangeLabel(rangeMode)}</div>
              <div className="cold-chain-meta">Filtro GPS: {minMovementM} m</div>
              <div className="cold-chain-meta">Umbral TEMP: {thresholdText(thresholds?.TEMP, "°C")}</div>
              <div className="cold-chain-meta">Umbral HUM: {thresholdText(thresholds?.HUM, "%HR")}</div>
            </div>
          </div>

          <div className="cold-chain-summary-grid">
            <div>
              <span>Temp. promedio</span>
              <strong>{fmtNumber(stats.tempAvg, 1)} °C</strong>
            </div>
            <div>
              <span>Humedad promedio</span>
              <strong>{fmtNumber(stats.humAvg, 1)} %HR</strong>
            </div>
            <div>
              <span>Puntos de ruta</span>
              <strong>{stats.count}</strong>
            </div>
            <div>
              <span>Alarmas</span>
              <strong style={{ color: stats.alarmCount ? "#b91c1c" : "#15803d" }}>{stats.alarmCount}</strong>
            </div>
            <div>
              <span>GPS originales</span>
              <strong>{stats.rawCount}</strong>
            </div>
            <div>
              <span>Duración</span>
              <strong>{durationText}</strong>
            </div>
          </div>

          <div className="cold-chain-last-points">
            <div className="cold-chain-section-title">{onlyAlarms ? "Últimos puntos fuera de rango" : "Últimas posiciones"}</div>
            {!listPoints.length ? (
              <div className="cold-chain-empty">{loading ? "Cargando recorrido..." : onlyAlarms ? "No hay puntos fuera de rango." : "No hay posiciones en este rango."}</div>
            ) : (
              listPoints
                .slice(-6)
                .reverse()
                .map((p) => (
                  <div className="cold-chain-point-row" key={p.id || `${p.ts}-${p.lat}-${p.lon}`}>
                    <div>
                      <strong>{fmtDT(p.ts)}</strong>
                      <span>{Number(p.lat).toFixed(6)} / {Number(p.lon).toFixed(6)}</span>
                    </div>
                    <div className="cold-chain-point-values">
                      <span>{fmtNumber(p.temp, 1)} °C</span>
                      <span>{fmtNumber(p.hum, 1)} %HR</span>
                      {p.is_alarm && <span style={{ color: "#b91c1c", fontWeight: 900 }}>ALARMA</span>}
                    </div>
                    {p.is_alarm && p.alarm_detail && (
                      <div style={{ gridColumn: "1 / -1", color: "#b91c1c", fontSize: 11, fontWeight: 750, marginTop: 3 }}>
                        {p.alarm_detail}
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="card glass cold-chain-map-card">
          <div className="cold-chain-map-head">
            <div>
              <strong>Recorrido GPS</strong>
              <span>Azul = recorrido · rojo = fuera de rango · verde = inicio · naranja = fin.</span>
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => downloadRoute("xlsx")}
                disabled={!!downloading || loading}
                title="Descargar el recorrido filtrado en Excel"
              >
                {downloading === "xlsx" ? "DESCARGANDO..." : "EXCEL"}
              </button>
              <button
                type="button"
                onClick={() => downloadRoute("kml")}
                disabled={!!downloading || loading}
                title="Descargar el recorrido filtrado para Google Earth"
              >
                {downloading === "kml" ? "DESCARGANDO..." : "KML"}
              </button>
              <button
                type="button"
                onClick={() => downloadRoute("pdf")}
                disabled={!!downloading || loading}
                title="Descargar reporte PDF de trazabilidad con resumen, ruta esquemática y alarmas"
              >
                {downloading === "pdf" ? "DESCARGANDO..." : "PDF"}
              </button>
              <button
                type="button"
                onClick={() => { loadTrack(); loadLatest(); }}
                disabled={loading}
              >
                {loading ? "ACTUALIZANDO..." : "ACTUALIZAR"}
              </button>
            </div>
          </div>
          <div ref={mapContainerRef} className="cold-chain-map" />
          <div className="cold-chain-map-footer">
            <span>
              {stats.count
                ? `${stats.count} puntos de ruta · ${stats.rawCount} GPS originales · ${stats.alarmCount} alarmas · filtro ${minMovementM} m${onlyAlarms ? " · mostrando solo alarmas" : ""}`
                : "Sin recorrido en el rango"}
            </span>
            <span>{lastRefresh ? `Consulta: ${fmtDT(lastRefresh)}` : ""}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
